import { TextKey, RouteName } from '@mews-commander/enums';
import { useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import {
    hasCurrentProfileAnyServicePrivileges,
    canCurrentProfileManageEnterpriseEmployees,
    canCurrentProfileConfigureEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { useNavigationSearchResult } from './useNavigationSearchResult';
import { AssistantNavigation } from './AssistantNavigation';
import { useNavigationPath } from './useNavigationPath';

export const useSettingsNavigation = (): AssistantNavigation[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const canConfigureEnterprise = useCommanderSelector<boolean>(canCurrentProfileConfigureEnterprise);
    const canManageEnterpriseEmployees = useCommanderSelector<boolean>(canCurrentProfileManageEnterpriseEmployees);
    const hasServicePrivileges = useCommanderSelector<boolean>(hasCurrentProfileAnyServicePrivileges);

    const localizeText = useLocalizeText();

    const parentPath = `${localizeText({ textKey: TextKey.Settings })}${UnicodeEntity.Space}${
        UnicodeEntity.ForwardSlash
    }${UnicodeEntity.Space}`;

    const employeeResult = useNavigationSearchResult(
        RouteName.UserManagementPropertyUsersIndex,
        TextKey.EmployeePlural,
        {},
        useNavigationPath(parentPath, TextKey.EmployeePlural)
    );
    const enterpriseResult = useNavigationSearchResult(
        RouteName.EnterpriseDetail,
        TextKey.Property,
        { enterpriseId },
        useNavigationPath(parentPath, TextKey.Property)
    );
    const serviceResult = useNavigationSearchResult(
        RouteName.ServiceIndex,
        TextKey.ServicePlural,
        {
            query: { OwnerId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.ServicePlural)
    );
    const textMessagingResult = useNavigationSearchResult(
        RouteName.TextMessaging,
        TextKey.TextMessaging,
        {
            query: { OwnerId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.TextMessaging)
    );
    const digitalKeyResult = useNavigationSearchResult(
        RouteName.DigitalKeysConfiguration,
        TextKey.DigitalKey,
        {
            query: { OwnerId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.DigitalKey)
    );
    const CheckInFormResult = useNavigationSearchResult(
        RouteName.CheckInForm,
        TextKey.CheckInForm,
        {
            query: { OwnerId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.CheckInForm)
    );

    return [
        canManageEnterpriseEmployees ? employeeResult : null,
        enterpriseId && canConfigureEnterprise ? enterpriseResult : null,
        hasServicePrivileges ? serviceResult : null,
        textMessagingResult,
        digitalKeyResult,
        CheckInFormResult,
    ].filter(Boolean) as AssistantNavigation[];
};
